/**
 * Personnel roster — admin + owner only.
 * Lists all staff with their current salary and status.
 * Add person modal, status toggle via ⋯ menu (intentional interaction only).
 * Click a row to navigate to the detail page.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../components/Icon'
import { useAuth, authHeaders } from '../lib/auth'
import { useTranslation } from 'react-i18next'

// ── types ─────────────────────────────────────────────────────────────────────

interface Person {
  person_id:                number
  full_name:                string
  email:                    string | null
  title:                    string | null
  employment_type:          'employee' | 'contractor' | 'part_time'
  status:                   'active' | 'inactive' | 'on_leave'
  start_date:               string | null
  end_date:                 string | null
  notes:                    string | null
  current_amount_usd_minor: number | null
  current_frequency:        string | null
  created_at:               string
}

// ── helpers ───────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  active:   'pill pill-mint',
  inactive: 'pill bg-ink-100 text-ink-500',
  on_leave: 'pill pill-honey',
}

const TYPE_LABEL: Record<string, string> = {
  employee:    'Employee',
  contractor:  'Contractor',
  part_time:   'Part-time',
}

const FREQ_LABEL: Record<string, string> = {
  monthly:  '/mo',
  biweekly: '/2wk',
  weekly:   '/wk',
  annual:   '/yr',
  hourly:   '/hr',
}

function fmtSalary(minor: number | null, freq: string | null): string {
  if (minor == null) return '—'
  const dollars = (minor / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
  return `${dollars}${FREQ_LABEL[freq ?? ''] ?? ''}`
}

// ── Add Person Modal ──────────────────────────────────────────────────────────

interface AddPersonModalProps { onClose: () => void }

function AddPersonModal({ onClose }: AddPersonModalProps) {
  const { t } = useTranslation()
  const { token } = useAuth()
  const qc = useQueryClient()

  const [form, setForm] = useState({
    full_name: '', email: '', title: '', employment_type: 'employee',
    start_date: '', status: 'active', notes: '',
  })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async (body: typeof form) => {
      const res = await fetch('/api/personnel', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['personnel'] }); onClose() },
    onError:   (e: Error) => setError(e.message),
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <h2 className="text-[15px] font-semibold">Add person</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="form-label">Full name *</label>
            <input className="form-input" value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="jane@…" />
            </div>
            <div>
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={set('title')} placeholder="Ops Manager" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Type</label>
              <select className="form-select" value={form.employment_type} onChange={set('employment_type')}>
<option value="employee">{t('personnel.type_employee')}</option>
                <option value="contractor">{t('personnel.type_contractor')}</option>
                <option value="part_time">Part-time</option>
              </select>
            </div>
            <div>
              <label className="form-label">Start date</label>
              <input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} />
            </div>
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-input min-h-[60px] resize-none" value={form.notes} onChange={set('notes')} />
          </div>

          {error && (
            <div className="text-sm text-flame-700 bg-flame-50 border border-flame-200 rounded-xl px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
          <button onClick={onClose} className="btn-ghost">{t('common.cancel')}</button>
          <button
            onClick={() => create.mutate(form)}
            disabled={!form.full_name.trim() || create.isPending}
            className="btn-primary"
          >
            {create.isPending ? 'Adding…' : 'Add person'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Row ⋯ menu ────────────────────────────────────────────────────────────────

function PersonMenu({ person }: { person: Person }) {
  const { token } = useAuth()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState<'deactivate' | 'reactivate' | null>(null)

  const patch = useMutation({
    mutationFn: async (body: Partial<Person>) => {
      const res = await fetch(`/api/personnel/${person.person_id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['personnel'] }); setConfirm(null); setOpen(false) },
  })

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-lg hover:bg-ink-50 text-ink-400 hover:text-ink-700"
      >
        <Icon name="more-horizontal" />
      </button>

      {open && !confirm && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-ink-200 py-1 z-20">
          {person.status !== 'inactive' ? (
            <button
              onClick={() => { setConfirm('deactivate') }}
              className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
            >
              Mark inactive
            </button>
          ) : (
            <button
              onClick={() => { setConfirm('reactivate') }}
              className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
            >
              Reactivate
            </button>
          )}
          {person.status === 'active' && (
            <button
              onClick={() => { patch.mutate({ status: 'on_leave' }); setOpen(false) }}
              className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
            >
              Mark on leave
            </button>
          )}
        </div>
      )}

      {confirm && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-ink-200 p-3 z-20">
          <p className="text-[13px] text-ink-700 mb-3">
            {confirm === 'deactivate'
              ? `Deactivate ${person.full_name}?`
              : `Reactivate ${person.full_name}?`}
          </p>
          <div className="flex gap-2">
            <button onClick={() => setConfirm(null)} className="btn-ghost text-xs flex-1">Cancel</button>
            <button
              onClick={() => patch.mutate({ status: confirm === 'deactivate' ? 'inactive' : 'active' })}
              disabled={patch.isPending}
              className={`flex-1 text-xs font-medium px-3 py-1.5 rounded-lg ${confirm === 'deactivate' ? 'bg-flame-600 text-white' : 'bg-mint-600 text-white'}`}
            >
              {patch.isPending ? '…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main View ─────────────────────────────────────────────────────────────────

export function PersonnelView() {
  const { t } = useTranslation()
  const { token } = useAuth()
  const navigate  = useNavigate()
  const [showAdd, setShowAdd]   = useState(false)
  const [filter,  setFilter]    = useState<'all' | 'active' | 'inactive'>('active')

  const { data: people = [], isLoading, isError, error } = useQuery<Person[]>({
    queryKey: ['personnel', filter],
    // Never retry on 4xx — auth errors won't fix themselves on retry
    retry: (count, err) => count < 2 && !/^(4\d\d|HTTP 4)/.test((err as Error).message),
    queryFn: async () => {
      const r = await fetch(`/api/personnel${filter !== 'all' ? `?status=${filter}` : ''}`, {
        headers: authHeaders(token),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.error ?? `HTTP ${r.status}`)
      }
      return r.json()
    },
  })

  const active   = people.filter(p => p.status === 'active').length
  const onLeave  = people.filter(p => p.status === 'on_leave').length
  const inactive = people.filter(p => p.status === 'inactive').length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Personnel</h1>
          <p className="text-sm text-ink-500 mt-0.5">{active} active · {onLeave} on leave · {inactive} inactive</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2">
          <Icon name="plus" className="w-4 h-4" /> Add person
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-ink-100 rounded-xl p-1 w-fit">
        {(['active', 'all', 'inactive'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === f ? 'bg-white shadow-sm text-ink-900' : 'text-ink-500 hover:text-ink-700'
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-ink-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-ink-400">
            <Icon name="loader-circle" className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-16 text-flame-600 gap-2">
            <Icon name="alert-circle" className="w-5 h-5" />
            <span className="text-sm">
              {(error as Error).message.includes('authentication') || (error as Error).message.includes('401')
                ? 'Session expired — please refresh the page to log in again.'
                : (error as Error).message}
            </span>
          </div>
        ) : people.length === 0 ? (
          <div className="py-16 text-center text-ink-400">
            <Icon name="briefcase" className="w-8 h-8 mx-auto mb-2 opacity-30" />
<p>{t('personnel.no_personnel')}</p>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wide">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Start date</th>
                <th className="px-4 py-3">Current salary</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {people.map(p => (
                <tr
                  key={p.person_id}
                  onClick={() => navigate(`/personnel/${p.person_id}`)}
                  className="hover:bg-ink-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900">{p.full_name}</div>
                    {p.title && <div className="text-ink-400 text-[11px]">{p.title}</div>}
                    {p.email && <div className="text-ink-400 text-[11px]">{p.email}</div>}
                  </td>
<td className="px-4 py-3 text-ink-500">{p.employment_type === 'employee' ? t('personnel.type_employee') : p.employment_type === 'contractor' ? t('personnel.type_contractor') : TYPE_LABEL[p.employment_type]}</td>
                  <td className="px-4 py-3">
                    <span className={STATUS_BADGE[p.status] ?? 'pill'}>{p.status.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-ink-500 mono">
                    {p.start_date ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-medium text-ink-800">
                    {fmtSalary(p.current_amount_usd_minor, p.current_frequency)}
                  </td>
                  <td className="px-4 py-3">
                    <PersonMenu person={p} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddPersonModal onClose={() => setShowAdd(false)} />}
    </div>
  )
}
